import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { CustomSelect } from '../../../../UI/CustomSelect/CustomSelect';
import { DatePicker } from '../../../../UI/DatePicker/DatePicker';
import { Input } from '../../../../UI/Input/Input';
import { Textarea } from '../../../../UI/Textarea/Textarea';
import { AddEventImage } from '../../../../components/AddEventImage/AddEventImage';
import { AddNewsHeader } from '../../../../components/AddNewsHeader/AddNewsHeader';
import { axiosInstance } from '../../../../helpers/axios';
import { INewsCard, SelectOption } from '../../../../types/types';
import { EVENT_ADDRESS_OPTIONS, EVENT_FORMAT_OPTIONS } from '../eventOptions';
import styles from './EditNews.module.scss';

export const EditNews = () => {
    const pathname = useLocation();
    const [newsInfo, setNewsInfo] = useState<INewsCard | null>(null);
    const newsId = pathname.pathname.split('/')[3];
    const newsUrl = `/v1/news/${newsId}/`;

    const [selectedImages, setSelectedImages] = useState<FileList | null>(null);

    const validImage = useMemo(() => {
        if (selectedImages?.length && selectedImages[0].size < 512000) {
            return true;
        }
        if (!selectedImages?.length) {
            return true;
        }
        return false;
    }, [selectedImages]);

    const initialValues = useMemo(() => {
        let datetime = '';
        if (newsInfo?.publish_date) {
            const now = new Date(newsInfo?.publish_date);

            const day = ('0' + now.getDate()).slice(-2);
            const month = ('0' + (now.getMonth() + 1)).slice(-2);

            datetime = now.getFullYear() + '-' + month + '-' + day;
        }
        return {
            title: newsInfo?.title ?? '',
            publish_date: datetime ?? '',
            text: newsInfo?.text ?? '',
            is_event: newsInfo?.is_event ?? false,
            // бэкенд отдаёт дату мероприятия уже в виде ГГГГ-ММ-ДД
            event_date: newsInfo?.event_date ?? '',
            event_format: newsInfo?.event_format ?? '',
            event_address: newsInfo?.event_address ?? '',
        };
    }, [newsInfo]);

    const [isLoading, setIsLoading] = useState(false);
    const formik = useFormik({
        enableReinitialize: true,
        validateOnMount: true,
        initialValues,
        onSubmit: async () => {
            setIsLoading(true);
            const dateArr = formik.values.publish_date;

            const formData = new FormData();

            formData.append('title', formik.values.title);
            formData.append('publish_date', dateArr);
            formData.append('text', formik.values.text.replace('↵', '\n'));
            formData.append('is_event', String(formik.values.is_event));
            if (formik.values.is_event) {
                formData.append('event_date', formik.values.event_date);
                formData.append('event_format', formik.values.event_format);
                formData.append('event_address', formik.values.event_address);
            }

            await axiosInstance
                .put(`/v1/news/${newsId}/`, formData)
                .then(() => {
                    toast.success('Новость успешно обновлена!');
                    formik.resetForm();
                    setSelectedImages(null);
                    setIsLoading(false);
                    window.history.go(-1);
                })
                .catch(() => {
                    toast.error('При обновлении новости произошла ошибка');
                    setIsLoading(false);
                });

            setIsLoading(false);
        },
        validationSchema: yup.object().shape({
            title: yup
                .string()
                .required('Поле обязательно')
                .max(100, 'Заголовок не должен содержать более 100 символов'),
            publish_date: yup.string().required('Поле обязательно'),
            // дата мероприятия нужна, только если стоит галочка
            event_date: yup.string().when('is_event', {
                is: true,
                then: (schema) => schema.required('Поле обязательно'),
            }),
            text: yup
                .string()
                .required('Поле обязательно')
                .max(4096, ' Превышен лимит символов'),
        }),
    });

    useEffect(() => {
        axiosInstance
            .get(newsUrl)
            .then((response) => {
                setNewsInfo(response?.data);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [newsUrl]);
    return (
        <form onSubmit={formik.handleSubmit} className={styles.addnews}>
            <AddNewsHeader
                dirty={formik.dirty}
                isValid={formik.isValid && validImage}
                isLoading={isLoading}
                onSubmit={formik.handleSubmit}
                resetForm={() => {
                    formik.resetForm();
                    window.history.go(-1);
                }}
                title={'Обновить новость'}
            />
            <AddEventImage
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                showImage={newsInfo?.images[0].path ?? null}
                showUpdateButton={false}
            />
            {!validImage ? (
                <span className={styles.validImage}>
                    Максимальный размер изображения 500Кб
                </span>
            ) : null}
            <div className={styles.addnews__content}>
                <Input
                    wrapperClassName={[styles.addnews_first]}
                    placeholder="Новость"
                    fieldClassName={styles.input__field}
                    inputHeader={'Название новости'}
                    headerClassName={styles.input__title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.title}
                    name={'title'}
                    errorMessage={formik.errors.title}
                    isValid={
                        formik.touched &&
                        !formik.errors.title &&
                        !!formik.values.title
                    }
                />
                <DatePicker
                    wrapperClassName={[styles.addnews_second]}
                    placeholder="02.03.2023"
                    headerText={'Дата'}
                    headerClassName={styles.input__title}
                    onChange={(e) => {
                        formik.handleChange(e);
                    }}
                    onBlur={formik.handleBlur}
                    value={formik.values.publish_date}
                    name={'publish_date'}
                    errorMessage={formik.errors.publish_date}
                    isValid={
                        formik.touched &&
                        !formik.errors.publish_date &&
                        !!formik.values.publish_date
                    }
                />
                <DatePicker
                    wrapperClassName={[styles.addnews_event]}
                    headerText={
                        <label className={styles.eventToggle}>
                            Это мероприятие
                            <input
                                type="checkbox"
                                name="is_event"
                                checked={formik.values.is_event}
                                onChange={formik.handleChange}
                            />
                        </label>
                    }
                    headerClassName={styles.input__title}
                    isDisabled={!formik.values.is_event}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.event_date}
                    name={'event_date'}
                    errorMessage={
                        formik.values.is_event
                            ? formik.errors.event_date
                            : undefined
                    }
                    isValid={
                        formik.values.is_event && !!formik.values.event_date
                    }
                />
                {formik.values.is_event ? (
                    <>
                        <CustomSelect
                            className={styles.addnews_half}
                            titleClass={styles.input__title}
                            title={'Формат мероприятия'}
                            borderRadius={'17px'}
                            placeholderSize={'15px'}
                            name={'event_format'}
                            options={EVENT_FORMAT_OPTIONS}
                            value={EVENT_FORMAT_OPTIONS.find(
                                (option) =>
                                    option.value === formik.values.event_format,
                            )}
                            onChange={(option: SelectOption) =>
                                formik.setFieldValue('event_format', option.value)
                            }
                            isSearchable={false}
                        />
                        <CustomSelect
                            className={styles.addnews_half}
                            titleClass={styles.input__title}
                            title={'Место проведения'}
                            borderRadius={'17px'}
                            placeholderSize={'15px'}
                            name={'event_address'}
                            options={EVENT_ADDRESS_OPTIONS}
                            value={EVENT_ADDRESS_OPTIONS.find(
                                (option) =>
                                    option.value === formik.values.event_address,
                            )}
                            onChange={(option: SelectOption) =>
                                formik.setFieldValue('event_address', option.value)
                            }
                            isSearchable={false}
                        />
                    </>
                ) : null}

                <Textarea
                    wrapperClassName={styles.addnews_third}
                    title={'Описание'}
                    headerClassName={styles.input__title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.text}
                    name={'text'}
                    errorMessage={formik.errors.text}
                    isValid={
                        formik.touched &&
                        !formik.errors.text &&
                        !!formik.values.text
                    }
                />
            </div>
        </form>
    );
};
